import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const VARIANTS = {
  horizon: {
    eyebrow: "The Horizon Circle",
    title: "Request the waitlist",
    agreement:
      "I understand membership is by application and I will hold the Circle's correspondence in confidence.",
    submit: "Join the waitlist",
    success: "You are on the waitlist. The Circle will be in touch.",
    focusPrefix: (context) => (context ? `Horizon · ${context}` : "The Horizon Circle"),
  },
  outer: {
    eyebrow: "Sybils & Society · The Outer Circle",
    title: "Join the Outer Circle mailing list",
    agreement:
      "I wish to receive the Outer Circle dispatch and will hold the Society's correspondence in confidence.",
    submit: "Join the Outer Circle",
    success: "Welcome to the Outer Circle. Watch for our dispatch.",
    focusPrefix: (context) =>
      context ? `Outer Circle · ${context}` : "The Outer Circle — Sybils & Society",
  },
};

// Warm capture modal — supports the Horizon Circle waitlist and the Sybils & Society Outer Circle mailing list.
export const HorizonWaitlist = ({ open, onClose, context, variant = "horizon" }) => {
  const cfg = VARIANTS[variant] || VARIANTS.horizon;
  const [form, setForm] = useState({ name: "", email: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "" });
      setAgree(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required.");
    if (!agree) return toast.error("Please confirm the discretion agreement.");
    setLoading(true);
    try {
      await joinWaitlist({
        name: form.name.trim(),
        email: form.email.trim(),
        focus: cfg.focusPrefix(context),
      });
      toast.success(cfg.success);
      onClose();
    } catch (err) {
      toast.error("Something went awry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="horizon-waitlist-dialog"
        >
          <div className="absolute inset-0 bg-[#0A0A0A]/60 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.45, ease }}
            className="relative w-full max-w-md rounded-3xl border border-[#0A0A0A]/10 bg-[#F4EFE6] p-8 shadow-2xl"
          >
            <button
              onClick={onClose}
              className="absolute right-5 top-5 text-[#0A0A0A]/40 transition-colors hover:text-[#C05C42]"
              data-testid="horizon-waitlist-close-btn"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            <p className="font-body text-[0.6rem] uppercase tracking-[0.25em] text-[#C05C42]">{cfg.eyebrow}</p>
            <h3 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-[#0A0A0A]">
              {cfg.title}
            </h3>
            {context && (
              <p className="mt-2 font-body text-sm text-[#0A0A0A]/60" data-testid="horizon-waitlist-context">
                {context}
              </p>
            )}

            <form onSubmit={submit} className="mt-7 space-y-4">
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                className="w-full rounded-xl border border-[#0A0A0A]/15 bg-[#FAF9F6] px-4 py-3 font-body text-sm text-[#0A0A0A] outline-none placeholder:text-[#0A0A0A]/35 focus:border-[#C05C42]"
                data-testid="horizon-waitlist-name"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                className="w-full rounded-xl border border-[#0A0A0A]/15 bg-[#FAF9F6] px-4 py-3 font-body text-sm text-[#0A0A0A] outline-none placeholder:text-[#0A0A0A]/35 focus:border-[#C05C42]"
                data-testid="horizon-waitlist-email"
              />
              <label className="flex cursor-pointer items-start gap-3" data-testid="horizon-waitlist-agree-label">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#C05C42]"
                  data-testid="horizon-waitlist-agree"
                />
                <span className="font-body text-xs leading-relaxed text-[#0A0A0A]/60">
                  {cfg.agreement}
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C05C42] px-6 py-3.5 font-body text-[0.7rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                data-testid="horizon-waitlist-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {cfg.submit}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default HorizonWaitlist;
